import { onDestroy } from "svelte";
import * as v from "valibot";
import { AppError, unexpectedError } from "../api/problem";
import { holdRelease, type ReleaseHold } from "../release/holds";

export interface Form<T extends Record<string, unknown>> {
  readonly values: T;
  readonly errors: Partial<Record<keyof T, string>>;
  readonly touched: Partial<Record<keyof T, boolean>>;
  readonly dirty: boolean;
  readonly busy: boolean;
  /** Form-level (non-field) error, e.g. a 409/412 banner with correlation id. */
  readonly formError: AppError | null;
  set: <K extends keyof T>(field: K, value: T[K]) => void;
  touch: (field: keyof T) => void;
  validate: () => boolean;
  submit: () => Promise<void>;
  reset: (values?: Partial<T>) => void;
  setFormError: (error: AppError | null) => void;
  dispose: () => void;
}

function cloneValue<T>(value: T): T {
  if (Array.isArray(value)) return value.map((entry) => cloneValue(entry)) as T;
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, cloneValue(entry)])
    ) as T;
  }
  return value;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && left.length === right.length
      && left.every((entry, index) => valuesEqual(entry, right[index]));
  }
  if (left === null || right === null || typeof left !== "object" || typeof right !== "object") {
    return false;
  }
  const leftEntries = Object.entries(left);
  const rightRecord = right as Record<string, unknown>;
  return leftEntries.length === Object.keys(rightRecord).length
    && leftEntries.every(([key, value]) => Object.hasOwn(rightRecord, key) && valuesEqual(value, rightRecord[key]));
}

export function createForm<T extends Record<string, unknown>>(
  schema: v.GenericSchema<unknown, T>,
  initial: T,
  onSubmit: (values: T) => Promise<void>
): Form<T> {
  const initialValues = cloneValue(initial);
  const state = $state<{
    values: T;
    errors: Partial<Record<keyof T, string>>;
    touched: Partial<Record<keyof T, boolean>>;
    dirty: boolean;
    busy: boolean;
    formError: AppError | null;
  }>({
    values: cloneValue(initialValues),
    errors: {},
    touched: {},
    dirty: false,
    busy: false,
    formError: null
  });

  let baseline = cloneValue(initialValues);
  let dirtyHold: ReleaseHold | null = null;
  let busyHold: ReleaseHold | null = null;

  function releaseDirtyHold(): void {
    dirtyHold?.();
    dirtyHold = null;
  }

  function releaseBusyHold(): void {
    busyHold?.();
    busyHold = null;
  }

  function syncDirty(): void {
    const dirty = !valuesEqual(state.values, baseline);
    state.dirty = dirty;
    if (dirty && dirtyHold === null) dirtyHold = holdRelease("dirty-form");
    else if (!dirty) releaseDirtyHold();
  }

  function dispose(): void {
    releaseDirtyHold();
    releaseBusyHold();
  }

  try {
    $effect(() => syncDirty());
    onDestroy(dispose);
  } catch (error) {
    // Unit-level consumers create forms without a component lifecycle and dispose explicitly.
    if (
      !(error instanceof Error)
      || (!error.message.includes("effect_orphan") && !error.message.includes("lifecycle_outside_component"))
    ) throw error;
  }

  function validate(): boolean {
    const result = v.safeParse(schema, state.values);
    if (result.success) {
      state.errors = {};
      return true;
    }
    const errors: Partial<Record<keyof T, string>> = {};
    for (const issue of result.issues) {
      const key = issue.path?.[0]?.key as keyof T | undefined;
      if (key !== undefined && errors[key] === undefined) errors[key] = issue.message;
    }
    state.errors = errors;
    return false;
  }

  return {
    get values() {
      return state.values;
    },
    get errors() {
      return state.errors;
    },
    get touched() {
      return state.touched;
    },
    get dirty() {
      return state.dirty;
    },
    get busy() {
      return state.busy;
    },
    get formError() {
      return state.formError;
    },
    set(field, value) {
      state.values = { ...state.values, [field]: value };
      syncDirty();
      if (state.touched[field]) validate();
    },
    touch(field) {
      state.touched = { ...state.touched, [field]: true };
    },
    validate,
    async submit() {
      if (state.busy) return;
      state.touched = Object.fromEntries(Object.keys(state.values).map((key) => [key, true])) as Partial<
        Record<keyof T, boolean>
      >;
      state.formError = null;
      if (!validate()) return;
      state.busy = true;
      busyHold = holdRelease("busy-form");
      try {
        await onSubmit(state.values);
      } catch (caught) {
        // Callers invoke submit() un-awaited from click handlers, so rethrowing a
        // non-AppError would surface as an unhandled rejection with nothing on
        // screen. Everything unrecognized becomes a correlated form-level error.
        const error = unexpectedError(caught);
        if (error.fieldErrors) {
          const mapped: Partial<Record<keyof T, string>> = { ...state.errors };
          for (const [field, message] of Object.entries(error.fieldErrors)) {
            mapped[field as keyof T] = message;
          }
          state.errors = mapped;
        } else {
          state.formError = error;
        }
      } finally {
        state.busy = false;
        releaseBusyHold();
      }
    },
    reset(values) {
      // Keep reset safe to call from a Svelte effect. Reading state.values here
      // would make the caller's effect depend on every form field and reset the
      // form again after each edit or successful submission.
      const nextValues = cloneValue({ ...initialValues, ...(values ?? {}) });
      state.values = nextValues;
      baseline = cloneValue(nextValues);
      state.dirty = false;
      releaseDirtyHold();
      state.errors = {};
      state.touched = {};
      state.formError = null;
    },
    setFormError(error) {
      state.formError = error;
    },
    dispose
  };
}
